import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  MENTAL_HEALTH_NEURODIVERSITY_CATEGORY_ID,
  ASSESSMENT_STATUS,
} from "../../utils/constants";
import {
  convertToLowerCase,
  groupAndSortAssessments,
} from "../../utils/utility-functions";

// Featured Assessment Card Component
const FeaturedAssessmentCard = ({
  assessment,
  onStartAssessment,
  isPremiumOrGuest,
}) => {
  const { t } = useTranslation();
  return (
    <div className="lg:col-span-3">
      <div className="rounded-2xl p-6 border border-insightful-blue h-full flex items-center justify-center">
        <div className="flex flex-col items-center text-center">
          <span className="text-sm text-purple-600 font-medium mb-2">
            {t("dashboard-b.assessmentcategory_card.featured")}
          </span>
          <h3 className="text-xl font-semibold text-insightful-black mb-2">
            {assessment.title}
          </h3>

          <button
            onClick={() => onStartAssessment(assessment)}
            className="bg-[#FE6634] hover:bg-orange-700 text-white px-8 py-3 rounded-full font-medium transition-colors"
          >
            {t("dashboard-b.assessmentcategory_card.start-btn")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Assessment Item Component
const AssessmentItem = ({
  assessment,
  onStartAssessment,
  assessmentIcons = [],
  assessmentAttemptStatuses = [],
  handleContinueTest,
  isPremiumOrGuest,
}) => {
  // Find matching icon for this assessment
  const getAssessmentIcon = () => {
    const matchingIcon = assessmentIcons.find(
      (iconItem) =>
        convertToLowerCase(assessment?.id).trim() ===
        convertToLowerCase(iconItem.key).trim()
    );
    return matchingIcon?.icon;
  };

  const iconSrc = getAssessmentIcon();

  // Find assessment status by matching assessmentId
  const getAssessmentStatus = () => {
    const statusItem = assessmentAttemptStatuses.find(
      (status) =>
        convertToLowerCase(status.assessmentId) ===
        convertToLowerCase(assessment.id)
    );

    return {
      assessmentAttemptId: statusItem?.assessmentAttemptId,
      status: statusItem?.status,
    };
  };

  const assessmentStatus = getAssessmentStatus();

  const inProgress =
    assessmentStatus?.status &&
    convertToLowerCase(assessmentStatus.status) ===
      convertToLowerCase(ASSESSMENT_STATUS.IN_PROGRESS);

  const completed =
    assessmentStatus?.status &&
    convertToLowerCase(assessmentStatus.status) ===
      convertToLowerCase(ASSESSMENT_STATUS.COMPLETED);

  const titleClass = inProgress
    ? "text-insightful-blue"
    : completed
      ? "text-insightful-purple opacity-40"
      : "text-insightful-purple";

  const handleClick = () => {
    if (inProgress && handleContinueTest) {
      handleContinueTest(
        assessment?.id,
        assessmentStatus?.assessmentAttemptId,
        isPremiumOrGuest
      );
    } else {
      onStartAssessment(assessment);
    }
  };

  return (
    <div
      className="flex items-center justify-start py-1 hover:bg-gray-50 rounded cursor-pointer w-full"
      onClick={handleClick}
    >
      <div className="flex items-center justify-start space-x-3 w-full">
        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
          {iconSrc ? (
            <img
              src={iconSrc}
              alt={`${assessment.title} icon`}
              className="w-4 h-4"
            />
          ) : (
            <span className="text-insightful-purple text-sm ">🧠</span>
          )}
        </div>
        <span className={`text-sm ${titleClass} text-left`}>
          {assessment.title}
        </span>
        <div className="flex items-center space-x-1 mr-2">
          {inProgress && <span className="text-blue-600">🅿️</span>}
          {completed && <span className="text-green-600">✅</span>}
        </div>
      </div>
    </div>
  );
};

// Assessment Category Card Component
const AssessmentCategoryCard = ({
  categoryName,
  assessments,
  onStartAssessment,
  assessmentIcons = [],
  assessmentAttemptStatuses = [],
  handleContinueTest,
  className = "",
  isPremiumOrGuest,
}) => {
  return (
    <div
      className={`border rounded-2xl  border-insightful-blue p-4 ${className}`}
    >
      <h3 className="text-lg font-semibold text-insightful-black text-left mb-4">
        {categoryName}
      </h3>
      <div className="space-y-3">
        {assessments.map((assessment) => (
          <AssessmentItem
            key={assessment.id}
            assessment={assessment}
            onStartAssessment={onStartAssessment}
            assessmentIcons={assessmentIcons}
            assessmentAttemptStatuses={assessmentAttemptStatuses}
            handleContinueTest={handleContinueTest}
            isPremiumOrGuest={isPremiumOrGuest}
          />
        ))}
      </div>
    </div>
  );
};

const TakeAssessmentModal = ({
  isOpen,
  onClose,
  allAssessments = [],
  assessmentIcons = [],
  assessmentAttemptStatuses = [],
  handleContinueTest,
  handleStartAssessment,
  isPremiumOrGuest,
  showOnlySpecificCategories = false,
  isAttemptUserAllAssessments,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get assessments excluding Mental Health & Neurodiversity
  const otherGroupedAssessments = groupAndSortAssessments(allAssessments, {
    excludeCategoryId: MENTAL_HEALTH_NEURODIVERSITY_CATEGORY_ID,
  });

  // Get Mental Health & Neurodiversity assessments by categoryId and sort by assessmentOrder
  const mentalHealthAssessments = groupAndSortAssessments(allAssessments, {
    groupByCategory: false,
    filterByCategoryId: MENTAL_HEALTH_NEURODIVERSITY_CATEGORY_ID,
  });

  // Get featured assessment: random not attempted assessment, or null if all completed
  const notAttemptedAssessments = allAssessments.filter((assessment) => {
    const statusItem = assessmentAttemptStatuses.find(
      (status) =>
        convertToLowerCase(status.assessmentId) ===
        convertToLowerCase(assessment.id),
    );
    return !statusItem;
  });

  const featuredAssessment =
    notAttemptedAssessments.length > 0
      ? notAttemptedAssessments[
          Math.floor(Math.random() * notAttemptedAssessments.length)
        ]
      : null;

  // Filter assessments to show only in progress or not attempted when showOnlySpecificCategories is true
  const filterAssessmentsByStatus = (assessments) => {
    if (!showOnlySpecificCategories) return assessments;
    return assessments.filter((assessment) => {
      const statusItem = assessmentAttemptStatuses.find(
        (status) =>
          convertToLowerCase(status.assessmentId) ===
          convertToLowerCase(assessment.id)
      );
      const status = statusItem?.status;
      return (
        !status ||
        convertToLowerCase(status) ===
          convertToLowerCase(ASSESSMENT_STATUS.IN_PROGRESS)
      );
    });
  };

  const handleStartAssessmentClick = (assessment) => {
    if (handleStartAssessment) {
      handleStartAssessment(assessment.id, isPremiumOrGuest);
    }
    onClose();
  };
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      <div
        className={`fixed transform bg-white shadow z-[1000] overflow-y-auto 
       ${
         isMobile
           ? "bottom-0 left-0 right-0 top-[15%] rounded-t-[20px]"
           : "left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[20px] max-w-6xl max-h-[90vh]"
       }
       w-full`}
      >
        <div className="p-6">
          {/* Header */}
          {!showOnlySpecificCategories && (
            <div className="mb-6">
              {/* Close Button Row - Above Title */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={onClose}
                  className="flex px-3 py-1 items-center min-w-max text-[16px] rounded-full text-white bg-[#805AED] border border-[#805AED] hover:bg-[#6a4cb8] focus:outline-none"
                >
                  <span>{t("dashboard.close_button")}</span>
                  <span className="text-[20px] ml-2">&times;</span>
                </button>
              </div>

              {/* Title Row */}
              <div className="flex items-start justify-start flex-col">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  {t("dashboard-b.assessmentcategory_card.begin_assessment")}
                </h2>
                <p className="text-gray-600">
                  {t("dashboard-b.assessmentcategory_card.sub_text")}
                </p>
              </div>
            </div>
          )}

          {/* Close button when showing specific categories */}
          {showOnlySpecificCategories && (
            <div className="flex justify-end mb-6">
              <button
                onClick={onClose}
                className="flex px-3 py-1 items-center min-w-max text-[16px] rounded-full text-white bg-[#805AED] border border-[#805AED] hover:bg-[#6a4cb8] focus:outline-none"
              >
                <span>{t("dashboard.close_button")}</span>
                <span className="text-[20px] ml-2">&times;</span>
              </button>
            </div>
          )}

          {/* Assessment Categories Grid */}
          <div className="space-y-6">
            {/* Assessment Categories Grid */}
            {!showOnlySpecificCategories && (
              <>
                {/* Row with Featured Assessment */}
                {featuredAssessment && !isAttemptUserAllAssessments && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                      {/* Featured Assessment - Takes 3 columns */}
                      <FeaturedAssessmentCard
                        assessment={featuredAssessment}
                        onStartAssessment={handleStartAssessmentClick}
                        isPremiumOrGuest={isPremiumOrGuest}
                        className="lg:col-span-3"
                      />
                      {/* Mental Health & Neurodiversity - Takes 2 columns */}
                      {mentalHealthAssessments.length > 0 && (
                        <AssessmentCategoryCard
                          categoryName={
                            mentalHealthAssessments[0]?.assessmentCategoryName
                          }
                          assessments={mentalHealthAssessments}
                          onStartAssessment={handleStartAssessmentClick}
                          assessmentIcons={assessmentIcons}
                          assessmentAttemptStatuses={assessmentAttemptStatuses}
                          handleContinueTest={handleContinueTest}
                          className="lg:col-span-2 h-full"
                          isPremiumOrGuest={isPremiumOrGuest}
                        />
                      )}
                    </div>

                    {/* Other Categories */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {Object.entries(otherGroupedAssessments).map(
                        ([categoryName, assessments]) => (
                          <AssessmentCategoryCard
                            key={categoryName}
                            categoryName={categoryName}
                            assessments={filterAssessmentsByStatus(assessments)}
                            onStartAssessment={handleStartAssessmentClick}
                            assessmentIcons={assessmentIcons}
                            assessmentAttemptStatuses={
                              assessmentAttemptStatuses
                            }
                            handleContinueTest={handleContinueTest}
                            isPremiumOrGuest={isPremiumOrGuest}
                          />
                        ),
                      )}
                    </div>
                  </>
                )}

                {/* When no featured assessment, show all categories in uniform grid */}
                {(!featuredAssessment || isAttemptUserAllAssessments) && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Mental Health & Neurodiversity */}
                    {mentalHealthAssessments.length > 0 && (
                      <AssessmentCategoryCard
                        categoryName={
                          mentalHealthAssessments[0]?.assessmentCategoryName
                        }
                        assessments={mentalHealthAssessments}
                        onStartAssessment={handleStartAssessmentClick}
                        assessmentIcons={assessmentIcons}
                        assessmentAttemptStatuses={assessmentAttemptStatuses}
                        handleContinueTest={handleContinueTest}
                        className="h-full"
                        isPremiumOrGuest={isPremiumOrGuest}
                      />
                    )}

                    {Object.entries(otherGroupedAssessments).map(
                      ([categoryName, assessments]) => (
                        <AssessmentCategoryCard
                          key={categoryName}
                          categoryName={categoryName}
                          assessments={filterAssessmentsByStatus(assessments)}
                          onStartAssessment={handleStartAssessmentClick}
                          assessmentIcons={assessmentIcons}
                          assessmentAttemptStatuses={assessmentAttemptStatuses}
                          handleContinueTest={handleContinueTest}
                          isPremiumOrGuest={isPremiumOrGuest}
                        />
                      ),
                    )}
                  </div>
                )}
              </>
            )}

            {/* Categories when showOnlySpecificCategories */}
            {showOnlySpecificCategories && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Mental Health & Neurodiversity when showOnlySpecificCategories */}
                {mentalHealthAssessments.length > 0 && (
                  <AssessmentCategoryCard
                    categoryName={
                      mentalHealthAssessments[0]?.assessmentCategoryName
                    }
                    assessments={filterAssessmentsByStatus(
                      mentalHealthAssessments,
                    )}
                    onStartAssessment={handleStartAssessmentClick}
                    assessmentIcons={assessmentIcons}
                    assessmentAttemptStatuses={assessmentAttemptStatuses}
                    handleContinueTest={handleContinueTest}
                    isPremiumOrGuest={isPremiumOrGuest}
                  />
                )}

                {Object.entries(otherGroupedAssessments).map(
                  ([categoryName, assessments]) => (
                    <AssessmentCategoryCard
                      key={categoryName}
                      categoryName={categoryName}
                      assessments={filterAssessmentsByStatus(assessments)}
                      onStartAssessment={handleStartAssessmentClick}
                      assessmentIcons={assessmentIcons}
                      assessmentAttemptStatuses={assessmentAttemptStatuses}
                      handleContinueTest={handleContinueTest}
                      isPremiumOrGuest={isPremiumOrGuest}
                    />
                  ),
                )}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-6 flex flex-wrap items-start justify-start gap-4 text-sm text-gray-600">
            <p className="text-insightful-black font-medium">
              {t("dashboard-b.assessmentcategory_card.legend-text")}
            </p>

            <span className="flex items-center">
              <span className="text-blue-600 mr-1">🅿️</span>{" "}
              {t("dashboard.in_progress")}
            </span>
            <span className="flex items-center">
              <span className="text-green-600 mr-1">✅</span>{" "}
              {t("dashboard.completed")}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

export default TakeAssessmentModal;
